/**
 * Device Fingerprinting Utility
 * Generates a unique fingerprint based on browser and device characteristics
 */

const getDeviceFingerprint = async () => {
  const components = [];

  // Screen information
  components.push(`screen:${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`);
  
  // Timezone
  components.push(`tz:${Intl.DateTimeFormat().resolvedOptions().timeZone}`);
  
  // Language
  components.push(`lang:${navigator.language}`);
  
  // Platform
  components.push(`platform:${navigator.platform}`);
  
  // User Agent (normalized)
  const ua = navigator.userAgent;
  const browserMatch = ua.match(/(Chrome|Safari|Firefox|Edge|Opera)\/[\d.]+/);
  const browser = browserMatch ? browserMatch[0] : 'Unknown';
  components.push(`browser:${browser}`);
  
  // Hardware concurrency (CPU cores)
  if (navigator.hardwareConcurrency) {
    components.push(`cores:${navigator.hardwareConcurrency}`);
  }
  
  // Device memory (if available)
  if (navigator.deviceMemory) {
    components.push(`memory:${navigator.deviceMemory}`);
  }
  
  // Touch support
  const touchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  components.push(`touch:${touchSupport}`);
  
  // Canvas fingerprint (simplified)
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('SAFER-i Device Fingerprint', 2, 2);
    const canvasData = canvas.toDataURL().slice(-50);
    components.push(`canvas:${canvasData}`);
  } catch {
    components.push('canvas:unavailable');
  }
  
  // WebGL renderer (if available)
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        components.push(`gpu:${renderer.substring(0, 50)}`);
      }
    }
  } catch {
    components.push('gpu:unavailable');
  }

  // Create hash from components
  const fingerprintString = components.join('|');
  const hash = await hashString(fingerprintString);
  
  return hash;
};

// Simple hash function using SubtleCrypto
const hashString = async (str) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
};

// Store fingerprint in localStorage for consistency
export const getStoredFingerprint = async () => {
  let fingerprint = localStorage.getItem('device_fingerprint');
  
  if (!fingerprint) {
    fingerprint = await getDeviceFingerprint();
    localStorage.setItem('device_fingerprint', fingerprint);
  }
  
  return fingerprint;
};

export default getDeviceFingerprint;
